import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, apiErr, pdfUrl } from "../lib/api";
import { money } from "../lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, AlertTriangle, XCircle, CheckCircle2, Lock, FileCheck2, ArrowLeft, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import ROEExtractPanel from "../components/ROEExtractPanel";
import { ROEOtherMonies, ROEConfirmFilingDialog, ROEFilingRecord, ROEExtractHistory } from "../components/ROEFilingTools";
import { useClientPerms } from "../lib/useClientPerms";
import { ROENotifyButtons, ROENotifyEmployerAll } from "../components/ROENotify";

const RS = {
  READY: "bg-emerald-100 text-emerald-700 border-emerald-300",
  WARNING: "bg-amber-100 text-amber-700 border-amber-300",
  BLOCKED: "bg-red-100 text-red-700 border-red-300",
  FINALIZED: "bg-black text-white border-black",
};

export default function ROEWorkspace() {
  const location = useLocation();
  const companyId = new URLSearchParams(location.search).get("company") || "";
  const [queue, setQueue] = useState(null);
  const roeEdit = useClientPerms().canEdit("t4");
  const [sel, setSel] = useState(null);
  const [det, setDet] = useState(null);
  const [reason, setReason] = useState("A00");
  const [firstDay, setFirstDay] = useState("");
  const [lastDay, setLastDay] = useState("");
  const [busy, setBusy] = useState(false);

  const loadQueue = useCallback(() => {
    if (!companyId) return;
    api.get(`/roe-workflow/${companyId}/queue`).then((r) => setQueue(r.data)).catch(() => setQueue(null));
  }, [companyId]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const loadDetail = useCallback((eid) => {
    const q = `reason=${reason}${firstDay ? `&first_day=${firstDay}` : ""}${lastDay ? `&last_day=${lastDay}` : ""}`;
    api.get(`/roe-workflow/${companyId}/${eid}?${q}`).then((r) => { setDet(r.data); setSel(eid); }).catch((e) => toast.error("Failed to load ROE"));
  }, [companyId, reason, firstDay, lastDay]);

  useEffect(() => { if (sel) loadDetail(sel); }, [reason, firstDay, lastDay]); // eslint-disable-line

  const finalize = async () => {
    setBusy(true);
    try {
      await api.post(`/roe-workflow/${companyId}/${sel}/finalize`, { reason, first_day: det.first_day, last_day: det.last_day });
      toast.success("ROE finalized"); loadDetail(sel); loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not finalize"); }
    finally { setBusy(false); }
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { user } = useAuth();
  const isMaster = user?.role === "super_admin" && (user?.staff_role || "master") === "master";
  const deleteRoe = async () => {
    const filed = det.filing_status === "FILED_CONFIRMED";
    const reason = window.prompt(`Delete this ${det.finalized ? "finalized " : ""}ROE for ${det.employee_name || "this employee"} and start over?${filed ? "\n\nWARNING: it was already filed with Service Canada — you will need to issue an AMENDED ROE in ROE Web." : ""}\n\nEnter a reason (required):`);
    if (reason === null) return;
    try {
      const r = await api.delete(`/roe-workflow/${det.roe_id}`, { data: { reason } });
      toast.success("ROE deleted — you can start over from the queue");
      if (r.data?.warning) toast.warning(r.data.warning, { duration: 12000 });
      loadDetail(sel); loadQueue(); setHistKey((k) => k + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [histKey, setHistKey] = useState(0);
  const filing = async (status) => {
    if (status === "FILED_CONFIRMED") { setConfirmOpen(true); return; }
    try { await api.post(`/roe-workflow/${det.roe_id}/filing`, { filing_status: status }); toast.success("Filing updated"); loadDetail(sel); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Failed"); }
  };
  const amend = async () => {
    const r = window.prompt("Amendment/replacement reason:"); if (!r) return;
    try { await api.post(`/roe-workflow/${det.roe_id}/amend`, { reason: r, first_day: det.first_day, last_day: det.last_day }); toast.success("ROE amended"); loadDetail(sel); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="ROE" />
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight">Records of Employment</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">Interruption of Earnings · Review & Finalize</p>
      </div>

      {!sel ? (
        <>
        <div data-testid="roe-queue" className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center justify-between gap-3">
            <span>ROE Review Queue{queue ? ` · ${queue.count}` : ""}</span>
            {roeEdit && <ROENotifyEmployerAll companyId={companyId} onDone={loadQueue} />}
          </div>
          {queue && queue.items.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No interruptions of earnings detected.</p>}
          {queue && queue.items.map((it) => (
            <button key={it.employee_id} data-testid={`roe-queue-item-${it.employee_id}`} onClick={() => loadDetail(it.employee_id)}
              className="w-full flex items-center gap-3 px-5 py-3 border-b border-border last:border-0 text-left hover:bg-neutral-50 transition-colors">
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{it.employee_name}</span>
              <span className="text-xs text-muted-foreground">{it.interruption?.kind}{it.termination_date ? ` · ${it.termination_date}` : ""}</span>
              <span className={`ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${RS[it.status === "FINALIZED" || it.status === "AMENDED" ? "FINALIZED" : "WARNING"]}`}>{it.status}</span>
            </button>
          ))}
        </div>
        <ROEExtractPanel companyId={companyId} canEdit={roeEdit} onDone={() => { loadQueue(); setHistKey((k) => k + 1); }} />
        <ROEExtractHistory companyId={companyId} refreshKey={histKey} />
        </>
      ) : det && (
        <div>
          <button onClick={() => { setSel(null); setDet(null); }} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-black mb-4"><ArrowLeft className="h-4 w-4" />Back to queue</button>
          <div data-testid="roe-detail" className="border border-black bg-white">
            <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-black/10">
              <span className="font-bold tracking-tight">{det.employee_name}</span>
              <span data-testid="roe-readiness-status" className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border ${RS[det.finalized ? "FINALIZED" : det.readiness_status] || RS.READY}`}>
                {det.finalized ? <Lock className="h-3.5 w-3.5" /> : det.readiness_status === "BLOCKED" ? <XCircle className="h-3.5 w-3.5" /> : det.readiness_status === "WARNING" ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {det.finalized ? "FINALIZED" : det.readiness_status}
              </span>
              {det.finalized && <span data-testid="roe-filing-status" className="text-[10px] font-mono uppercase tracking-widest border border-black px-2 py-0.5">{det.filing_status}</span>}
              <div className="ml-auto flex gap-2">
                <a data-testid="roe-pdf-button" href={pdfUrl(`/roe/${companyId}/${sel}/pdf?reason=${reason}&first_day=${det.first_day}&last_day=${det.last_day}`)} target="_blank" rel="noreferrer" className="border border-black px-3 py-2 text-sm hover:bg-black hover:text-white">ROE PDF</a>
                {roeEdit && !det.finalized && <button data-testid="roe-finalize-button" disabled={busy || det.blocker_count > 0} onClick={finalize} className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm hover:bg-neutral-800 disabled:opacity-40"><Lock className="h-4 w-4" />Finalize</button>}
                {roeEdit && det.finalized && <>
                  <button data-testid="roe-exported" onClick={() => filing("EXPORTED")} className="border border-black px-3 py-2 text-sm hover:bg-black hover:text-white">Mark exported</button>
                  <button data-testid="roe-confirm-filing" onClick={() => filing("FILED_CONFIRMED")} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-2 text-sm hover:bg-neutral-800"><FileCheck2 className="h-4 w-4" />Confirm filing</button>
                  <button data-testid="roe-amend-button" onClick={amend} className="border border-amber-500 text-amber-700 px-3 py-2 text-sm hover:bg-amber-500 hover:text-white">Amend/Replace</button>
                  <ROENotifyButtons det={det} onDone={() => loadDetail(sel)} />
                </>}
                {isMaster && det.finalized && det.roe_id && <button data-testid="roe-delete-button" onClick={deleteRoe} title="Delete this ROE and start over (Admin only)" className="inline-flex items-center gap-1.5 border border-red-600 text-red-700 px-3 py-2 text-sm hover:bg-red-600 hover:text-white"><Trash2 className="h-4 w-4" />Delete & start over</button>}
              </div>
            </div>

            {det.reporting_changed && (
              <div data-testid="roe-reporting-changed" className="px-5 py-3 bg-red-50 border-b border-red-200 text-sm text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /><span className="font-semibold">Reporting changed after finalization</span> — payroll changed. Amend the finalized ROE.
              </div>
            )}

            {det.finalized && <ROEFilingRecord det={det} />}
            {det.finalized && det.roe_id && (
              <div className="px-5 pt-4 space-y-4">
                <ROEOtherMonies det={det} canEdit={roeEdit} onSaved={() => loadDetail(sel)} />
                <ROEExtractPanel companyId={companyId} roeIds={[det.roe_id]} canEdit={roeEdit} compact onDone={() => loadDetail(sel)} />
              </div>
            )}
            {confirmOpen && <ROEConfirmFilingDialog det={det} onClose={() => setConfirmOpen(false)} onDone={() => { setConfirmOpen(false); loadDetail(sel); }} />}
            <div className="px-5 py-4 grid sm:grid-cols-3 gap-4 text-sm">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Reason (Block 16)</span>
                <select data-testid="roe-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} disabled={det.finalized} className="mt-1 w-full border border-input bg-white px-3 py-2">
                  {Object.entries(det.reason_codes).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">First day worked</span>
                <input data-testid="roe-first-day" type="date" value={det.first_day} onChange={(e) => setFirstDay(e.target.value)} disabled={det.finalized} className="mt-1 w-full border border-input bg-white px-3 py-2" /></label>
              <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Last day paid</span>
                <input data-testid="roe-last-day" type="date" value={det.last_day} onChange={(e) => setLastDay(e.target.value)} disabled={det.finalized} className="mt-1 w-full border border-input bg-white px-3 py-2" /></label>
            </div>

            <div className="px-5 pb-4 grid sm:grid-cols-3 gap-4">
              <div className="border border-border p-3"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Insurable earnings</div><div data-testid="roe-insurable-earnings" className="text-xl font-black tabular-nums">{money(det.insurable_earnings)}</div></div>
              <div className="border border-border p-3"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Insurable hours</div><div data-testid="roe-insurable-hours" className="text-xl font-black tabular-nums">{det.insurable_hours}</div></div>
              <div className="border border-border p-3"><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Pay periods</div><div className="text-xl font-black tabular-nums">{det.period_count}</div></div>
            </div>

            {det.exceptions.length > 0 && !det.finalized && (
              <div className="px-5 pb-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Readiness</div>
                <ul className="space-y-1.5">
                  {det.exceptions.map((e, i) => (
                    <li key={i} data-testid={`roe-exception-${e.code}`} className={`flex items-start gap-2 text-sm ${e.severity === "BLOCKER" ? "text-red-800" : "text-amber-800"}`}>
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${e.severity === "BLOCKER" ? "bg-red-500" : "bg-amber-500"}`} />{e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {det.amendments?.length > 0 && (
              <div className="px-5 pb-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Amendment / replacement history</div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {det.amendments.map((a, i) => <li key={i} data-testid={`roe-amendment-${i}`}>v{a.from_version}→v{a.to_version} · {a.reason} · {a.amended_by}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
